import * as React from 'react';
import CardContainer from '../../../components/CardContainer';
import { observer } from 'mobx-react-lite';
import StepperFooter from '../../../components/StepperFooter';
import CategoryKeywordModel from '../models/CategoryKeywordModel';
import { Trans, useTranslation } from 'react-i18next';
import {
  Checkbox,
  ConfigProvider,
  Form,
  Input,
  message,
  Select,
  Typography
} from 'antd';
import ActivityEntity from '../domain/ActivityEntity';
import AntdCustomCheckbox from '../../../components/buttons/AntdCustomCheckbox';
import CustomTagsField from '../../../components/CustomTagsField';
import BusinessModel from '../../FindYourBusiness/models/BusinessModel';
import { BodyText } from '../../../components/ui/Typography';
import HelpBoxCompanionModel from '../../../models/HelpBoxCompanionModel';
import { AnimatePresence, motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../components/animations/StepAnimations';

const { Text } = Typography;

interface FCProps {
  stepData: {};
}

const ActivitiesStep: React.FC<FCProps> = observer(() => {
  const { t, i18n } = useTranslation();
  const [isStepLoading, setIsStepLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);

  const [form] = Form.useForm();

  const activities = CategoryKeywordModel.activities;
  const allCustomCheckedActivities = activities.filter(
    (activity) => activity.isChecked && activity.isCustomActivity
  );
  const allNonCustomCheckedActivities = activities.filter(
    (activity) => activity.isChecked && !activity.isCustomActivity
  );

  const disabledUncheckedActivities = CategoryKeywordModel.isMaxActivitySelected;

  const helpBoxTitleMessage = t('Why is that important?');
  const helpBoxParagraphMessage = t('Often a corporate category is not enough to describe all business areas of a company. Therefore, select additional categories that fit your company or give your own activities to.');

  React.useEffect(() => {
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  const handleNextStep = async () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={staggerContainerVariants}
        >
          <CardContainer title={t('Choose further corporate activities')}>
            <motion.div variants={fadeInUpVariants}>
              <BodyText style={{ textAlign: 'center' }}>
                <Trans
                  i18nKey="activitiesStep.description1"
                  defaults="In addition to <b>{{category}}</b>, do you also work in further corporate activities?"
                  components={{ b: <b /> }}
                  values={{ category: BusinessModel.getWizardData().category }}
                />
              </BodyText>
            </motion.div>
            
            <div className="mb-7 sm:text-center">
              <div className="flex flex-col">
                <div>
                  <ConfigProvider
                    theme={{
                      token: {
                        fontSize: 12
                      }
                    }}>
                    <div className="mb-6"></div>
                  </ConfigProvider>
                  <div className="flex flex-col gap-[24px]">
                    <motion.div 
                      variants={fadeInUpVariants}
                      className="flex flex-wrap gap-4 justify-start sm:justify-center"
                    >
                      {activities
                        .filter((activity) => !activity.isCustomActivity)
                        .map((activity, index) => {
                          return (
                            <AntdCustomCheckbox
                              key={index}
                              appearance="basic"
                              disabled={
                                disabledUncheckedActivities && !activity.isChecked
                              }
                              checked={activity.isChecked}
                              onChange={(e) => {
                                activity.setChecked(e.target.checked);
                                if (e.target.checked) {
                                  CategoryKeywordModel.removeCustomActivityByName(activity.displayName.toLowerCase());
                                }
                              }}
                              text={activity.displayName}
                            />
                          );
                        })}
                    </motion.div>
                    <motion.div 
                      variants={fadeInUpVariants}
                      className="flex sm:justify-center"
                    >
                      <CustomTagsField
                        form={form}
                        formItemName="customActivities"
                        initialValues={activities
                          .filter((activity) => activity.isCustomActivity)
                          .map((activity) => activity.displayName)}
                        addButtonLabel={t('Add your own activity')}
                        addFurtherButtonLabel={t('Add further activity')}
                        tagsTitle={t('Select your category :')}
                        duplicateMessage={t('This activity already exists')}
                        maxCount={
                          disabledUncheckedActivities
                            ? 3 - allNonCustomCheckedActivities.length
                            : 3
                        }
                        disabled={disabledUncheckedActivities}
                        mode="select"
                        placeholder={t('Type to start with the search')}
                        existingValues={activities
                          .filter(
                            (activity) =>
                              activity.isChecked && !activity.isCustomActivity
                          )
                          .map((activity) => activity.displayName.toLowerCase())}
                        onChange={(values) => {
                          // Determine if any value is a JSON string (add), otherwise it's a delete
                          const jsonIndex = values.findIndex((area) => {
                            try {
                              JSON.parse(area);
                              return true;
                            } catch {
                              return false;
                            }
                          });

                          if (jsonIndex !== -1) {
                            // Add logic: add the JSON activity
                            const area = values[jsonIndex];
                            const value = JSON.parse(area);
                            CategoryKeywordModel.addNewActivity(
                              new ActivityEntity({
                                checked: true,
                                id: value.id,
                                displayName: value.displayName,
                                name: value.name,
                                keywords: value.keywords || [],
                                subcategories: value.subcategories || [],
                                isCustom: true
                              })
                            );
                          } else {
                            // Delete logic: figure out which activity was removed
                            // Get current custom activities
                            const currentCustomActivities = activities
                              .filter((activity) => activity.isCustomActivity)
                              .map((activity) => activity.displayName);

                            // Find which activity is missing
                            const deleted = currentCustomActivities.find(
                              (name) => !values.includes(name)
                            );
                            if (deleted) {
                              CategoryKeywordModel.removeCustomActivityByName(deleted.toLowerCase());
                            }
                          }
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
            
            <motion.div variants={fadeInUpVariants}>
              <StepperFooter
                buttonId="category-save-next"
                buttonGaEvent="category-save-next"
                loading={isStepLoading}
                onFormsValidationSuccess={async (formData, nextStep, toStep) => {
                  setIsStepLoading(true);

                  try {
                    await handleNextStep();
                    // Delay to allow exit animation to complete
                    setTimeout(async () => {
                      await CategoryKeywordModel.moveToNextStep();
                    }, 300);
                  } catch (error) {
                    message.error(
                      t(
                        'An error occurred while fetching keywords for custom areas. Please try again.'
                      )
                    );
                    setIsVisible(true);
                    setIsStepLoading(false);
                  }
                }}
              />
            </motion.div>
          </CardContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default ActivitiesStep;
